import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { CustomCellProps } from 'components/common/grid/';
import useBundleTranslation from 'i18n';
import { getCellWithTooltip } from 'components/common/grid/cell';

// Singleton — one polling interval for all generating rows in the same grid.
// First generating cell starts the interval; last one stops it.
let _generatingCount = 0;
let _pollingInterval: ReturnType<typeof setInterval> | null = null;
let _reloadFn: (() => void) | null = null;

function registerGenerating(reload: () => void) {
    _generatingCount++;
    _reloadFn = reload;
    if (!_pollingInterval) {
        _pollingInterval = setInterval(() => _reloadFn?.(), 5000);
    }
}

function unregisterGenerating() {
    _generatingCount = Math.max(0, _generatingCount - 1);
    if (_generatingCount === 0 && _pollingInterval) {
        clearInterval(_pollingInterval);
        _pollingInterval = null;
        _reloadFn = null;
    }
}

interface InsightNameCellProps extends CustomCellProps {
    generating?: boolean;
}

export default function InsightNameCell({
    text,
    tooltip,
    href,
    popupType,
    popupMode,
    popupConfig,
    setPopupUrl,
    setPopupConfig,
    setPopupType,
    setPopupMode,
    t,
    subText,
    rawTooltipHtml,
    setPopupComponent,
    setPopupComponentProps,
    component,
    popupComponentProps,
    generating,
    reloadGridData,
}: InsightNameCellProps) {
    const { t: tBundle } = useBundleTranslation('main');
    const tFn = t ?? tBundle;
    const textValue = text ? String(text) : '';

    useEffect(() => {
        if (!generating || !reloadGridData) return;
        registerGenerating(reloadGridData);
        return () => unregisterGenerating();
    }, [generating, reloadGridData]);

    const onClick = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        if (setPopupType) setPopupType(popupType ?? 'page');
        if (setPopupConfig) setPopupConfig(popupConfig ?? {});
        if (setPopupUrl) setPopupUrl(href ?? '');
        if (setPopupMode) setPopupMode(popupMode ?? 'new');
        if (setPopupComponent) setPopupComponent(component ?? '');
        if (setPopupComponentProps) setPopupComponentProps(popupComponentProps ?? {});
    };

    const button = (
        <Stack>
            <Box
                component="a"
                onClick={onClick}
                sx={{ cursor: 'pointer', color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
            >
                {textValue}
            </Box>
            {subText && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                    {generating && <CircularProgress size={10} thickness={5} />}
                    {subText}
                </Typography>
            )}
        </Stack>
    );

    if (tooltip) {
        return getCellWithTooltip(button, tooltip, rawTooltipHtml, tFn);
    }

    return button;
}
